import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, DatePicker, Input, Button, Space, Row, Col, Statistic } from 'antd';
import { PhoneOutlined, SearchOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import axios from '../services/axiosConfig';
import { API_BASE_URL } from '../constants/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SUPPORT_CALL_LOG_API = `${API_BASE_URL}/support-call-logs`;

const SupportCallLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [stats, setStats] = useState({ today: 0, total: 0 });

    const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = { page, limit: pageSize };
            if (search) params.search = search;
            if (dateRange[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
            if (dateRange[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');

            const { data } = await axios.get(SUPPORT_CALL_LOG_API, { params });
            if (data.success) {
                setLogs(data.data.logs || []);
                setPagination(p => ({
                    ...p,
                    current: page,
                    pageSize,
                    total: data.data.pagination?.total || 0,
                }));

                // Tính stats
                const allToday = (data.data.logs || []).filter(l =>
                    dayjs(l.CalledAt).isSame(dayjs(), 'day')
                ).length;
                setStats({ today: allToday, total: data.data.pagination?.total || 0 });
            }
        } catch (e) {
            console.error('Lỗi tải log:', e);
        } finally {
            setLoading(false);
        }
        console.log(logs);
    }, [search, dateRange]);

    useEffect(() => { fetchLogs(); }, []);

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 60,
            render: (_, __, idx) => (pagination.current - 1) * pagination.pageSize + idx + 1,
        },
        {
            title: 'Khách thuê',
            key: 'tenant',
            render: (_, r) => (
                <Space>
                    <UserOutlined style={{ color: '#999' }} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{r.TenantName || '—'}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{r.TenantID || ''}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Số điện thoại gọi đến',
            dataIndex: 'PhoneNumber',
            key: 'PhoneNumber',
            render: p => p ? (
                <Space>
                    <PhoneOutlined style={{ color: '#52c41a' }} />
                    <a href={`tel:${p}`}>{p}</a>
                </Space>
            ) : '—',
        },
        {
            title: 'Ghi chú',
            dataIndex: 'Note',
            key: 'Note',
            render: n => <Text type="secondary">{n || '—'}</Text>,
        },
        {
            title: 'Thời gian liên hệ',
            dataIndex: 'CalledAt',
            key: 'CalledAt',
            width: 180,
            sorter: (a, b) => new Date(a.CalledAt) - new Date(b.CalledAt),
            defaultSortOrder: 'descend',
            render: t => {
                const d = dayjs(t);
                const isToday = d.isSame(dayjs(), 'day');
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 13 }}>{d.format('HH:mm DD/MM/YYYY')}</Text>
                        {isToday && <Tag color="orange" style={{ fontSize: 10 }}>Hôm nay</Tag>}
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '16px' }}>
            <Title level={4} style={{ marginBottom: 16 }}>
                <PhoneOutlined style={{ marginRight: 8, color: '#ff6600' }} />
                Lịch sử liên hệ hỗ trợ
            </Title>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Tổng lượt liên hệ"
                            value={stats.total}
                            prefix={<PhoneOutlined />}
                            valueStyle={{ color: '#ff6600' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="Hôm nay"
                            value={stats.today}
                            prefix={<PhoneOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Bộ lọc */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={12} align="middle">
                    <Col flex="1">
                        <Input
                            placeholder="Tìm theo tên khách thuê hoặc số điện thoại..."
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <RangePicker
                            format="DD/MM/YYYY"
                            placeholder={['Từ ngày', 'Đến ngày']}
                            onChange={val => setDateRange(val || [null, null])}
                        />
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={() => fetchLogs(1, pagination.pageSize)}
                            >
                                Tìm kiếm
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    setSearch('');
                                    setDateRange([null, null]);
                                    fetchLogs(1, pagination.pageSize);
                                }}
                            >
                                Làm mới
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Bảng dữ liệu */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={logs}
                    rowKey="ID"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: total => `Tổng ${total} lượt liên hệ`,
                        pageSizeOptions: ['10', '20', '50'],
                        onChange: (page, pageSize) => fetchLogs(page, pageSize),
                    }}
                    locale={{ emptyText: 'Chưa có lượt liên hệ hỗ trợ nào' }}
                />
            </Card>
        </div>
    );
};

export default SupportCallLogs;
